import { useMemo, useState } from "react";
import { UserMinus } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { getTiers, personnelRecordFor } from "../lib/personnel";
import Modal from "./Modal";

// Admin-only removal of a capacity row that has NO Personnel record — the
// spreadsheet-import leftovers sitting on the capacity matrix and leave grid.
// Anyone with a Personnel record is departed on the Personnel page instead, so
// this control never renders for them.
export default function RemovePersonButton({ person, testidSuffix = "" }) {
  const { rawData, refresh } = useData();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("tbd");
  const [tier, setTier] = useState("");
  const [account, setAccount] = useState(null);

  const rows = useMemo(() => {
    const out = [];
    for (const p of rawData?.projects || []) {
      for (const a of p.allocations || []) {
        if (a.personId !== person?.id) continue;
        out.push({
          project: p.name || p.id,
          pm: Object.values(a.load || {}).reduce((t, v) => t + (Number(v) || 0), 0),
          status: a.status,
        });
      }
    }
    return out;
  }, [rawData, person]);

  const leaveWeeks = useMemo(
    () => Object.values(rawData?.leave?.[person?.id] || {}).reduce((t, v) => t + (Number(v) || 0), 0),
    [rawData, person]
  );
  const tiers = useMemo(() => getTiers(rawData), [rawData]);
  const record = personnelRecordFor(rawData, person);

  if (user?.role !== "admin" || !person || record) return null;

  const pmTotal = Math.round(rows.reduce((t, r) => t + r.pm, 0) * 100) / 100;
  const projectNames = [...new Set(rows.map((r) => r.project))];

  const start = async () => {
    setMode(rows.length ? "tbd" : "remove");
    setTier(person.tier || "");
    setAccount(null);
    setOpen(true);
    // A capacity row with no Personnel record can still belong to someone who
    // works here — surface their login at the point of decision.
    try {
      const { data } = await api.get("/users");
      const list = Array.isArray(data) ? data : data?.users || [];
      const name = (person.name || "").trim().toLowerCase();
      const local = name.replace(/\s+/g, ".");
      setAccount(
        list.find(
          (u) =>
            (u.name || "").trim().toLowerCase() === name ||
            (u.email || "").split("@")[0].toLowerCase() === local
        ) || null
      );
    } catch {
      setAccount(null);
    }
  };

  const confirmRemove = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/data/people/${encodeURIComponent(person.id)}/remove`, {
        allocations: rows.length ? mode : "remove",
        tier,
      });
      setOpen(false);
      await refresh();
      const d = res.data;
      toast.success(
        `Removed ${d.name}. ${d.allocationRows} allocation row${d.allocationRows === 1 ? "" : "s"} ` +
        `${d.allocations === "tbd" ? "converted to TBD" : "removed"} · restore from Backups if needed.`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove this person");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); start(); }}
        className="inline-flex items-center justify-center h-11 w-11 shrink-0 text-[var(--soft-fg)] opacity-60 hover:opacity-100 hover:text-[#6B2F2A] hover:bg-[#FBF3F2] focus-visible:opacity-100 focus-visible:text-[#6B2F2A] transition-colors"
        title={`Remove ${person.name} from capacity and leave (no Personnel record)`}
        aria-label={`Remove ${person.name} from the capacity matrix and leave grid`}
        data-testid={`remove-person-${person.id}${testidSuffix}`}
      >
        <UserMinus size={14} />
      </button>

      <Modal
        open={open}
        onClose={() => !busy && setOpen(false)}
        width="max-w-2xl"
        testid="remove-person-dialog"
      >
        <div className="eyebrow text-[#6B2F2A] mb-2">Remove from capacity</div>
        <div className="font-display text-lg text-[var(--ink)] mb-3">
          Remove {person.name} from the capacity matrix, the leave grid and the people list?
        </div>

        <div className="text-sm text-[var(--muted-fg)] space-y-2 mb-5" data-testid="remove-person-holdings">
          <div className="font-mono-num text-xs">
            No Personnel record — this row came from the original spreadsheet import.
          </div>
          <div>
            They hold <strong>{rows.length} project allocation{rows.length === 1 ? "" : "s"}</strong>
            {rows.length ? ` totalling ${pmTotal} person-month${pmTotal === 1 ? "" : "s"} across ${projectNames.join(", ")}` : ""}
            {" and "}
            <strong>{leaveWeeks ? `${leaveWeeks} leave week${leaveWeeks === 1 ? "" : "s"}` : "no leave entries"}</strong>.
          </div>
        </div>

        {account && (
          <div
            className="border border-[#6B2F2A] bg-[#FBF3F2] p-3 mb-5 text-sm text-[var(--ink)]"
            data-testid="remove-person-account-warning"
          >
            <strong>{person.name} has {account.status === "active" ? "an active" : "a"} login</strong>{" "}
            ({account.email} · {(account.role || "").replace(/_/g, " ")}). People who still work at
            the firm should keep their capacity row — check before removing.
          </div>
        )}

        {rows.length > 0 && (
          <div className="border border-[var(--rule)] p-4 mb-5 space-y-3" data-testid="remove-person-alloc-choice">
            <div className="eyebrow">What happens to their allocations</div>
            <label className="flex gap-3 items-start cursor-pointer text-sm">
              <input
                type="radio"
                name="alloc-mode"
                checked={mode === "tbd"}
                onChange={() => setMode("tbd")}
                className="mt-1"
                data-testid="remove-person-mode-tbd"
              />
              <span>
                <span className="text-[var(--ink)]">Convert to TBD tier rows</span> — the monthly
                values stay on each project (and in project margins) as an unfilled role. Because
                the row is no longer tied to a person it will not appear on the capacity matrix.
              </span>
            </label>
            {mode === "tbd" && (
              <div className="pl-7">
                <select
                  value={tier}
                  onChange={(e) => setTier(e.target.value)}
                  className="field-input text-xs"
                  data-testid="remove-person-tier"
                >
                  <option value="">Any tier — TBD</option>
                  {tiers.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            )}
            <label className="flex gap-3 items-start cursor-pointer text-sm">
              <input
                type="radio"
                name="alloc-mode"
                checked={mode === "remove"}
                onChange={() => setMode("remove")}
                className="mt-1"
                data-testid="remove-person-mode-remove"
              />
              <span>
                <span className="text-[var(--ink)]">Remove the allocations entirely</span> — the
                monthly values are deleted from those projects.
              </span>
            </label>
          </div>
        )}

        <div className="text-sm text-[var(--muted-fg)] mb-6">
          Their leave entries are removed with them. A snapshot is saved first — one click from
          Backups restores everything exactly.
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setOpen(false)}
            disabled={busy}
            className="px-4 py-2 border border-[var(--rule)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)] disabled:opacity-40"
            data-testid="remove-person-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmRemove}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#6B2F2A] text-white text-xs font-display tracked-tight uppercase hover:opacity-90 disabled:opacity-40"
            data-testid="remove-person-confirm"
          >
            <UserMinus size={12} /> {busy ? "Removing…" : "Remove person"}
          </button>
        </div>
      </Modal>
    </>
  );
}
